import { supabase } from '@/integrations/supabase/client';
import { StimulusContent } from '@/types';
import { handleDatabaseError } from '@/lib/errorHandler';

export const stimulusService = {
  /**
   * Buscar próximo estímulo (com rotação)
   */
  async getNextStimulus(
    category?: string,
    excludeTypes?: StimulusContent['contentType'][]
  ): Promise<StimulusContent | null> {
    try {
      let query = supabase
        .from('stimulus_content')
        .select('*')
        .eq('is_active', true);

      if (category) {
        query = query.eq('category', category);
      }

      if (excludeTypes && excludeTypes.length > 0) {
        query = query.not('content_type', 'in', `(${excludeTypes.join(',')})`);
      }

      // Ordenar por menos usado e mais antigo
      query = query.order('use_count', { ascending: true })
        .order('last_used_at', { ascending: true, nullsFirst: true })
        .limit(1);

      const { data, error } = await supabase
        .from('stimulus_content')
        .select('*')
        .eq('is_active', true)
        .order('use_count', { ascending: true })
        .limit(10); // Pegar top 10 menos usados

      if (error) throw error;
      if (!data || data.length === 0) return null;

      // Filtrar por categoria se fornecida
      let filtered = data;
      if (category) {
        filtered = data.filter(d => d.category === category || d.category === 'geral');
      }

      // Excluir tipos se fornecido
      if (excludeTypes && excludeTypes.length > 0) {
        filtered = filtered.filter(d => !excludeTypes.includes(d.content_type as any));
      }

      if (filtered.length === 0) return null;

      // Escolher aleatoriamente entre os menos usados
      const randomIndex = Math.floor(Math.random() * Math.min(3, filtered.length));
      const selected = filtered[randomIndex];

      // Marcar como usado
      await this.markAsUsed(selected.id);

      return {
        id: selected.id,
        contentType: selected.content_type as StimulusContent['contentType'],
        contentUrl: selected.content_url,
        contentText: selected.content_text,
        category: selected.category,
        durationSeconds: selected.duration_seconds,
        lastUsedAt: selected.last_used_at,
        useCount: selected.use_count,
        isActive: selected.is_active,
        createdAt: selected.created_at
      };
    } catch (error) {
      handleDatabaseError(error);
      return null;
    }
  },

  /**
   * Marcar estímulo como usado
   */
  async markAsUsed(stimulusId: string): Promise<void> {
    try {
      // Buscar use_count atual e incrementar
      const { data: current } = await supabase
        .from('stimulus_content')
        .select('use_count')
        .eq('id', stimulusId)
        .single();

      const { error } = await supabase
        .from('stimulus_content')
        .update({
          use_count: (current?.use_count || 0) + 1,
          last_used_at: new Date().toISOString()
        })
        .eq('id', stimulusId);

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao marcar estímulo como usado:', error);
    }
  },

  /**
   * Buscar todos os estímulos ativos
   */
  async getAllActiveStimuli(): Promise<StimulusContent[]> {
    try {
      const { data, error } = await supabase
        .from('stimulus_content')
        .select('*')
        .eq('is_active', true)
        .order('use_count', { ascending: true });

      if (error) throw error;

      return (data || []).map(d => ({
        id: d.id,
        contentType: d.content_type as StimulusContent['contentType'],
        contentUrl: d.content_url,
        contentText: d.content_text,
        category: d.category,
        durationSeconds: d.duration_seconds,
        lastUsedAt: d.last_used_at,
        useCount: d.use_count,
        isActive: d.is_active,
        createdAt: d.created_at
      }));
    } catch (error) {
      handleDatabaseError(error);
      return [];
    }
  }
};
